// @mui
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
// auth
import { useAuthContext } from '../../auth/useAuthContext';
// components
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from 'src/api/auth';
import { userApi } from 'src/api/user';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { getFirebaseErrorMessage } from 'src/utils/getFirebaseErrorMessage';
import { isValidPhoneNumber } from 'src/utils/phoneValidation';
import { CreateUserDto } from 'woofwoof-api';
import Iconify from '../../components/iconify';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

type Props = {
  onPhoneAuthChange?: (isPhoneAuth: boolean) => void;
};

export default function AuthWithSocial({ onPhoneAuthChange }: Props) {
  const { mutateAsync } = useMutation({
    mutationFn: (createUserDto: CreateUserDto) =>
      userApi.userControllerCreateUser({ createUserDto }),
  });
  const { loginWithGoogle } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneError, setPhoneError] = useState('');

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      if (loginWithGoogle) {
        const authResponse = await loginWithGoogle();
        await mutateAsync({
          firstName: authResponse.user?.displayName?.split(' ')[0] || '',
          lastName: authResponse.user?.displayName?.split(' ')[1] || '',
          avatar: authResponse.user?.photoURL || '',
          terms: false,
        });
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(getFirebaseErrorMessage(error.code) || error, {
        variant: 'error',
      });
    }
  };

  const handlePhoneLoginClick = () => {
    setShowPhoneAuth(true);
  };

  // Notify parent when phone auth state changes
  useEffect(() => {
    onPhoneAuthChange?.(showPhoneAuth);
  }, [showPhoneAuth, onPhoneAuthChange]);

  const handleSendCode = useCallback(async () => {
    // Validate phone number format
    if (!isValidPhoneNumber(phoneNumber, false)) {
      setPhoneError(
        translateKey("validation.phoneInvalid"),
      );
      return;
    }

    setIsLoading(true);
    setPhoneError('');

    try {
      await authApi.authControllerSendPhoneVerificationCode({
        sendPhoneVerificationDto: {
          phoneNumber,
        },
      });

      setStep('code');
      setResendTimer(30);
      enqueueSnackbar(translateKey("auth.phoneLoginCodeSent"), {
        variant: 'success',
      });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          translateKey("auth.phoneLoginSendCodeError"),
        {
          variant: 'error',
        },
      );
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, enqueueSnackbar]);

  const handleVerifyCode = async () => {
    setIsLoading(true);

    try {
      const result = await authApi.authControllerVerifyPhoneCode({
        verifyPhoneCodeDto: {
          phoneNumber,
          verificationCode,
        },
      });

      // Type assertion needed until API generates proper response types
      const data = result.data as any as {
        success: boolean;
        message: string;
        customToken?: string;
      };

      if (!data.success) {
        throw new Error(data.message || 'Invalid verification code');
      }

      if (!data.customToken) {
        throw new Error('No authentication token received');
      }

      const { getAuth, signInWithCustomToken } = await import('firebase/auth');
      const auth = getAuth();
      const userCredential = await signInWithCustomToken(
        auth,
        data.customToken,
      );

      await new Promise((resolve) => setTimeout(resolve, 500));
      const idToken = await userCredential.user.getIdToken();

      await mutateAsync({
        firstName: '',
        lastName: '',
        phoneNumber: userCredential.user.phoneNumber || phoneNumber,
        terms: false,
      });

      navigate(PATH_DASHBOARD.general.updateAccount);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        translateKey("hardcoded.nieprawidlowy_kod_weryfikacyjny");
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    setStep('phone');
    setVerificationCode('');
    handleSendCode();
  };

  return (
    <div>
      {!showPhoneAuth ? (
        <>
          <Stack direction="row" justifyContent="center" spacing={2}>
            <LoadingButton
              disabled={isLoading}
              fullWidth
              color="inherit"
              size="large"
              type="submit"
              variant="soft"
              onClick={handleGoogleLogin}
              startIcon={<Iconify icon="logos:google-icon" color="#DF3E30" />}
            >
              <span>{translateKey("hardcoded.zaloguj_sie_przez_google")}</span>
            </LoadingButton>
          </Stack>

          <Stack
            direction="row"
            justifyContent="center"
            spacing={2}
            sx={{ mt: 2 }}
          >
            <Button
              disabled={isLoading}
              fullWidth
              color="inherit"
              size="large"
              variant="outlined"
              onClick={handlePhoneLoginClick}
              startIcon={<Iconify icon="eva:phone-fill" />}
            >
              <span>{translateKey("hardcoded.zaloguj_sie_numerem_telefonu")}</span>
            </Button>
          </Stack>
        </>
      ) : (
        <Box>
          <TextField
            fullWidth
            label={translateKey("auth.fieldPhone")}
            placeholder="+48123456789"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              setPhoneError('');
            }}
            disabled={isLoading || step === 'code'}
            error={!!phoneError}
            helperText={phoneError}
            sx={{ mb: 2 }}
          />

          {step === 'phone' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {translateKey("hardcoded.smsVerificationCodeInfo")}
            </Alert>
          )}

          {step === 'code' && (
            <>
              <TextField
                fullWidth
                label={translateKey("auth.phoneLoginVerificationCodeLabel")}
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={isLoading}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">{translateKey("hardcoded.nie_otrzymales_kodu")}</Typography>
                <Button
                  size="small"
                  onClick={handleResendCode}
                  disabled={resendTimer > 0 || isLoading}
                >
                  {resendTimer > 0
                    ? translateKey("hardcoded.resendInSeconds", { seconds: resendTimer })
                    : translateKey("auth.verifyCodeResend")}
                </Button>
              </Box>
            </>
          )}

          <Stack direction="row" spacing={2}>
            {step === 'code' && (
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setShowPhoneAuth(false);
                  setStep('phone');
                  setPhoneNumber('');
                  setVerificationCode('');
                }}
                disabled={isLoading}
              >{translateKey("common.cancel")}</Button>
            )}
            <LoadingButton
              fullWidth
              size="large"
              type="button"
              variant="contained"
              loading={isLoading}
              onClick={step === 'phone' ? handleSendCode : handleVerifyCode}
              disabled={step === 'phone' ? !phoneNumber : !verificationCode}
            >
              {step === 'phone' ? translateKey("hardcoded.wyslij_kod") : translateKey("auth.phoneLoginVerifyCode")}
            </LoadingButton>
          </Stack>

          {step === 'phone' && (
            <Button
              fullWidth
              size="small"
              variant="text"
              onClick={() => {
                setShowPhoneAuth(false);
                setPhoneNumber('');
              }}
              disabled={isLoading}
              sx={{ mt: 1 }}
            >{translateKey("hardcoded.wroc_do_innych_metod_logowania")}</Button>
          )}
        </Box>
      )}

      {!showPhoneAuth && (
        <Divider
          sx={{
            my: 3,
            typography: 'overline',
            color: 'text.disabled',
            '&::before, ::after': {
              borderTopStyle: 'dashed',
            },
          }}
        >
          LUB
        </Divider>
      )}
    </div>
  );
}
